import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCoupon } from "../contexts/CouponContext";
import "./CouponBanner.css";

export default function CouponBanner() {
  const { availableCoupons, loading } = useCoupon();
  const [currentCouponIndex, setCurrentCouponIndex] = useState(0);
  const [copiedCode, setCopiedCode] = useState(null);

  // Convert coupons object to array for easier handling
  const couponsArray = Object.values(availableCoupons || {});

  // Rotate through coupons every 8 seconds
  useEffect(() => {
    if (couponsArray.length > 1) {
      const interval = setInterval(() => {
        setCurrentCouponIndex((prevIndex) => 
          (prevIndex + 1) % couponsArray.length
        );
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [couponsArray.length]);

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  // Don't render if loading or no coupons
  if (loading || couponsArray.length === 0) {
    return null;
  }

  const currentCoupon = couponsArray[currentCouponIndex];

  if (!currentCoupon) {
    return null;
  }

  const formatDiscount = (coupon) => {
    if (coupon.type === 'percentage') {
      return `${coupon.discount}%`;
    } else {
      return `₹${coupon.discount}`;
    }
  };

  const formatMinAmount = (amount) => {
    return `₹${amount}`;
  };

  return (
    <div 
      className="coupon-banner" 
      role="region" 
      aria-label="Special offer coupon banner"
    >
      <div className="coupon-banner-content">
        {/* Left section - Special Offer text */}
        <div className="coupon-banner-text">
          <span className="coupon-title">Special Offer</span>
          <span className="coupon-message">GET OFFER</span>
        </div>
        
        {/* Right section - Coupon code and details */}
        <div className="coupon-right-section">
          {/* Top row - Use Code and Shop Now */}
          <div className="coupon-top-row">
            <div className="coupon-code-container">
              <span className="coupon-code-label">Use Code:</span>
              <button 
                className={`coupon-code ${copiedCode === currentCoupon.code ? 'copied' : ''}`}
                onClick={() => copyToClipboard(currentCoupon.code)}
                title="Click to copy code"
              >
                {currentCoupon.code}
                {copiedCode === currentCoupon.code && (
                  <span className="copy-feedback">✓ Copied!</span>
                )}
              </button>
            </div>
            
            <Link 
              to="/collections" 
              className="coupon-shop-btn"
            >
              Shop Now
            </Link>
          </div>
          
          {/* Bottom row - Discount details */}
          <div className="coupon-details">
            <span className="discount-amount">{formatDiscount(currentCoupon)} OFF</span>
            <span className="min-amount">Min. {formatMinAmount(currentCoupon.minAmount)}</span>
            {currentCoupon.maxDiscount && (
              <span className="max-discount">Max. {formatMinAmount(currentCoupon.maxDiscount)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Progress indicator for multiple coupons */}
      {couponsArray.length > 1 && (
        <div className="coupon-progress">
          {couponsArray.map((_, index) => (
            <div
              key={index}
              className={`progress-dot ${index === currentCouponIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
